import React, { useEffect, useState } from "react";

import type {
    LinksFunction,
    LoaderFunction,
    MetaFunction,
} from "@remix-run/node";
import { json } from "@remix-run/node";
import type { ShouldRevalidateFunctionArgs } from "@remix-run/react";
import {
    Links,
    Meta,
    Outlet,
    Scripts,
    ScrollRestoration,
    useLoaderData,
    useRouteError,
} from "@remix-run/react";
import { captureRemixErrorBoundaryError } from "@sentry/remix";
import {
    dehydrate,
    HydrationBoundary,
    QueryClient,
    QueryClientProvider,
    hydrate,
} from "@tanstack/react-query";
// eslint-disable-next-line import/no-duplicates
import sonnerStylesInline from "sonner/dist/styles.css?inline";
// eslint-disable-next-line import/no-duplicates
import sonnerStylesUrl from "sonner/dist/styles.css?url";
import { useDehydratedState } from "use-dehydrated-state";

import { AnalyticsScreenTracker } from "~/components/analytics-screen-tracker";
import { HydrationErrorMonitor } from "~/components/hydration-error-monitor";
import { ConfirmDialogProvider } from "~/components/ui/confirm";
import { Toaster as Sonner } from "~/components/ui/sonner";
import { Toaster } from "~/components/ui/toaster";
import { TooltipProvider } from "~/components/ui/tooltip";
import { getOptionalAuth } from "~/config/asyncStore.server";
import { AnalyticsProvider } from "~/context/AnalyticsContext";
import { LoaderProvider } from "~/context/LoaderContext";
// import { ScreenSizeProvider } from "~/context/ScreenSizeContext";
import { SupabaseSessionProvider } from "~/context/SupabaseSessionContext";
// import { UserAgentContext } from "~/context/UserAgentContext";
import type { ScreenSize } from "~/hooks/use-screen-size";
import { isSentryEnabled } from "~/lib/env.shared";
import { getBrowserEnv } from "~/utils.server/env";

import { withCors } from "./config/cors";
import { UserAgentContext } from "./context/UserAgentContext";
import { prefetchAccountFeatureFlags } from "./hooks/queries/account-feature-flags.server";
import { prefetchUserRelationships } from "./hooks/queries/user-relationships.server";
import tailwindStyles from "./index.css?url";
import { ensureSupabaseReady } from "./integrations/supabase/server-init";
import { createSupabaseServerClient } from "./integrations/supabase/ssr.server";
import { prefetchUserProfile } from "./lib/user";
import { parseUserAgent } from "./utils.server/user-agent";

export const links: LinksFunction = () => [
    { rel: "stylesheet", href: tailwindStyles },
    { rel: "stylesheet", href: sonnerStylesUrl },
];

export const meta: MetaFunction = () => [
    // { title: "IFS Companion" },
    {
        name: "description",
        content: "Clarity, continuity, and connection in your IFS journey.",
    },
    {
        name: "viewport",
        content:
            "width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=5",
    },
];

// Helper to map numeric viewport width to Tailwind-like breakpoints
const mapWidthToScreenSize = (width: number | undefined): string => {
    if (!width || Number.isNaN(width)) return "xs";

    if (width >= 1536) return "2xl";
    if (width >= 1280) return "xl";
    if (width >= 1024) return "lg";
    if (width >= 768) return "md";
    if (width >= 640) return "sm";
    return "xs";
};

export const loader: LoaderFunction = withCors(async ({ request }) => {
    // Ensure Supabase is ready before any app rendering
    await ensureSupabaseReady();

    // Create SSR Supabase client
    const { supabase, headers } = createSupabaseServerClient(request);

    const queryClient = new QueryClient();

    const auth = getOptionalAuth();

    // Prefetch the supabase session using the same query key and function as SupabaseSessionContext
    const sessionPrefetch = queryClient.prefetchQuery({
        queryKey: ["supabase-session"],
        queryFn: async () => {
            // Get session from SSR client which handles cookies automatically
            const {
                data: { session },
            } = await supabase.auth.getSession();
            return session;
        },
    });

    const userProfilePrefetch = prefetchUserProfile(queryClient, auth!);
    const relationshipsPrefetch = prefetchUserRelationships(queryClient, auth!);
    const featureFlagsPrefetch = prefetchAccountFeatureFlags(
        queryClient,
        auth!,
    );

    await Promise.all([
        sessionPrefetch,
        userProfilePrefetch,
        relationshipsPrefetch,
        featureFlagsPrefetch,
    ]);

    const userAgentString = request.headers.get("user-agent");
    const userAgent = parseUserAgent(userAgentString);

    // Try to read Client-Hint headers sent by the browser for viewport width
    const viewportWidthHeader =
        request.headers.get("sec-ch-viewport-width") ??
        request.headers.get("viewport-width");

    const initialScreenSize = mapWidthToScreenSize(
        viewportWidthHeader ? Number(viewportWidthHeader) : undefined,
    ) as ScreenSize;

    const response = json(
        {
            env: getBrowserEnv(),
            dehydratedState: dehydrate(queryClient),
            userAgent,
            initialScreenSize,
        },
        {
            headers: {
                ...Object.fromEntries(headers.entries()),
                // Ask browsers that support Client-Hints to send viewport width
                "Accept-CH": "Width, Viewport-Width",
                "Critical-CH": "Width, Viewport-Width",
            },
        },
    );
    return response;
});

// AIDEV-NOTE: Prevent loader revalidation on fetcher/action submissions
export function shouldRevalidate({
    currentUrl,
    nextUrl,
    defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) {
    // Only revalidate on actual navigation (URL changes)
    if (currentUrl.pathname === nextUrl.pathname) {
        return false;
    }

    // Let default behavior handle param changes and navigations
    return defaultShouldRevalidate;
}

export const ErrorBoundary = () => {
    const error = useRouteError();
    if (isSentryEnabled()) captureRemixErrorBoundaryError(error);

    useEffect(() => {}, [error]);

    // AIDEV-NOTE: Enhanced error handling for Promise-related SSR errors
    const errorMessage = error instanceof Error ? error.message : String(error);

    // Check if it's a Promise-related React error (common SSR issue)
    if (
        errorMessage.includes("Objects are not valid as a React child") &&
        errorMessage.includes("[object Promise]")
    ) {
        console.error("React Promise Error caught in ErrorBoundary:", error);
        // Return a client-side only recovery component
        return (
            <div className="flex min-h-screen items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-amber-600">
                        Loading Error
                    </h1>
                    <p className="mt-2 text-gray-600">
                        The page encountered a loading error. Refreshing...
                    </p>
                    <script
                        dangerouslySetInnerHTML={{
                            __html: `
                            // Auto-refresh once to recover from SSR error
                            if (!sessionStorage.getItem('error_reload_attempted')) {
                                sessionStorage.setItem('error_reload_attempted', 'true');
                                setTimeout(() => window.location.reload(), 500);
                            }
                        `,
                        }}
                    />
                </div>
            </div>
        );
    }

    // Check if it's a Supabase initialization error
    if (
        errorMessage.includes("Supabase") ||
        errorMessage.includes("SUPABASE")
    ) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-red-600">
                        Database Connection Error
                    </h1>
                    <p className="mt-2 text-gray-600">
                        Unable to connect to the database. Please try again
                        later.
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-4 rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // In development, show a detailed error with stack trace for easier debugging
    if (process.env.NODE_ENV === "development") {
        return (
            <div className="flex min-h-screen items-center justify-center p-4">
                <div className="w-full max-w-2xl rounded-lg bg-red-50 p-6 shadow">
                    <h1 className="text-2xl font-bold text-red-700">
                        Application Error
                    </h1>
                    <p className="mt-2 break-all font-mono text-sm text-red-800">
                        {errorMessage}
                    </p>
                    {error instanceof Error && error.stack && (
                        <pre className="mt-4 max-h-96 overflow-auto whitespace-pre-wrap rounded bg-red-100 p-4 text-xs text-red-900">
                            {error.stack}
                        </pre>
                    )}
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-6 rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                    >
                        Reload
                    </button>
                </div>
            </div>
        );
    }

    return <div>Something went wrong</div>;
};

export default function App() {
    const loaderData = useLoaderData<typeof loader>();
    const { env, userAgent, initialScreenSize } = loaderData;
    // AIDEV-NOTE: useDehydratedState must be called conditionally to avoid SSR hook errors
    const dehydratedState = useDehydratedState();
    const [queryClient] = useState(() => {
        const qc = new QueryClient({
            defaultOptions: {
                queries: {
                    staleTime: 60 * 1000,
                    // Prevent refetches during hydration
                    refetchOnWindowFocus: false,
                    refetchOnMount: false,
                },
            },
        });

        // If we received dehydrated state from the server we can hydrate the
        // freshly-created client so that queries are immediately available.
        if (dehydratedState) {
            try {
                hydrate(qc, dehydratedState);
            } catch (e) {}
        }

        return qc;
    });

    // Add effect to monitor sonner style element
    useEffect(() => {
        if (typeof window !== "undefined") {
            const sonnerStyle = document.getElementById("sonner-inline");
        }
    }, []);

    return (
        <html lang="en" dir="ltr" className="h-full">
            {/*
             * We deliberately suppress hydration warnings for the entire
             * <head> element.  During development Vite (and some
             * third-party libraries) may inject additional <style> tags
             * into <head> *before* React gets the chance to run its
             * hydration pass.  Because the server-rendered HTML obviously
             * contains no such tags, React detects a mismatch and logs
             * recoverable hydration errors for the affected siblings
             * (<title>, <meta>, <link>, …).  The actual markup is still
             * perfectly fine – React just needs to be told that it is
             * allowed to reconcile minor differences.
             *
             * Adding `suppressHydrationWarning` at the <head> level keeps
             * the console clean without hiding *real* hydration problems
             * in the application body.
             */}
            <head suppressHydrationWarning>
                <Links />
                <Meta />
                {/*
                 * Always include the inline Sonner stylesheet so that the
                 * DOM structure of <head> is identical on the server and
                 * on the client during the initial hydration pass.
                 *
                 * Rendering it conditionally only on the server caused a
                 * mismatch (the server HTML contained the <style> element
                 * while the client render did not), which in turn broke
                 * React's hydration algorithm and resulted in the
                 * "Expected server HTML to contain a matching <title> in
                 * <head>" error.
                 *
                 * We can safely leave this element in place on the client
                 * because the same CSS rules are also loaded via the
                 * regular <link> tag that Remix injects through <Links />.
                 * Having them twice does not hurt, and keeps hydration
                 * happy.  If at some point we want to remove the inline
                 * variant after hydration we can do so in an effect, but
                 * for now the duplicate is negligible in size (~2 KB).
                 */}
                <style
                    id="sonner-inline"
                    dangerouslySetInnerHTML={{ __html: sonnerStylesInline }}
                    data-ssr="true"
                    suppressHydrationWarning
                />
            </head>
            <body className="h-full">
                {/* <HydrationDetector /> */}
                <QueryClientProvider client={queryClient}>
                    {/* <ScreenSizeProvider initialScreenSize={initialScreenSize}> */}
                    <HydrationBoundary state={dehydratedState}>
                        <UserAgentContext.Provider value={userAgent}>
                            <SupabaseSessionProvider>
                                <AnalyticsProvider>
                                    {/* AIDEV-NOTE: Track page/screen views on client navigation */}
                                    <AnalyticsScreenTracker />
                                    {/* AIDEV-NOTE: Monitor and report hydration errors in development */}
                                    <HydrationErrorMonitor />
                                    <LoaderProvider>
                                        <TooltipProvider>
                                            <Toaster />
                                            <Sonner />
                                            <ConfirmDialogProvider>
                                                <Outlet />
                                                <></>
                                            </ConfirmDialogProvider>
                                        </TooltipProvider>
                                    </LoaderProvider>
                                </AnalyticsProvider>
                            </SupabaseSessionProvider>
                        </UserAgentContext.Provider>
                    </HydrationBoundary>
                    {/* </ScreenSizeProvider> */}
                </QueryClientProvider>
                {/* AIDEV-NOTE: Wrap scripts in div to avoid hydration errors */}
                <div suppressHydrationWarning>
                    {/* Portal root for LoaderContext - must exist before Scripts */}
                    <div id="loader-portal-root" />
                    <ScrollRestoration />
                    <Scripts />
                    <script
                        dangerouslySetInnerHTML={{
                            __html: `window.env = ${JSON.stringify(env)}`,
                        }}
                    />
                </div>
            </body>
        </html>
    );
}
